import { BlogPost, BlogCategory, CloudinaryBlogImage } from '../types/Blog';
import { API_BASE_URL } from './apiBase';
import { studioOrder } from './blogPresentation';

const normalizeCloudinaryImage = (image: any): CloudinaryBlogImage | undefined => {
  if (!image) return undefined;

  if (typeof image === 'string') {
    return {
      publicId: image,
      url: image,
      secure_url: image.startsWith('https://') ? image : undefined,
    };
  }

  const url = image.url || image.secure_url || image.secureUrl || image.path || '';
  const publicId = image.publicId || image.public_id || image.publicID || image.asset_id || image.id || url;

  if (!publicId && !url) {
    return undefined;
  }

  return {
    publicId,
    public_id: image.public_id,
    secure_url: image.secure_url || (url.startsWith('https://') ? url : undefined),
    url,
    width: image.width,
    height: image.height,
    alt: image.alt || '',
    caption: image.caption || '',
  };
};

const normalizeImageArray = (images: any): CloudinaryBlogImage[] => {
  if (!Array.isArray(images)) return [];
  return images
    .map(image => normalizeCloudinaryImage(image))
    .filter((image): image is CloudinaryBlogImage => Boolean(image));
};

function categorySlugsFrom(rawPost: any): string[] {
  const listed = Array.isArray(rawPost?.categories) ? rawPost.categories.map((cat: any) => String(cat)) : [];
  const primary = String(rawPost?.category || listed[0] || '').trim();
  const slugs: string[] = [];
  [primary, ...listed].forEach((slug) => {
    const value = String(slug || '').trim();
    if (value && !slugs.includes(value)) slugs.push(value);
  });
  return slugs;
}

export const normalizeBlogPost = (rawPost: any): BlogPost => {
  const heroImage = normalizeCloudinaryImage(rawPost.heroImage || rawPost.featuredImage);
  const galleryImages = normalizeImageArray(rawPost.galleryImages || rawPost.images);
  const categories = categorySlugsFrom(rawPost);

  const createdAt = rawPost.createdAt || new Date().toISOString();
  const updatedAt = rawPost.updatedAt || createdAt;
  const publishedAt = rawPost.publishedAt || createdAt;

  const savedOrder = Number(rawPost.order);
  const basePost: BlogPost = {
    id: String(rawPost.id || rawPost._id || Date.now()),
    order: Number.isFinite(savedOrder) && savedOrder > 0 ? savedOrder : undefined,
    title: rawPost.title || 'Untitled Post',
    slug: rawPost.slug || '',
    excerpt: rawPost.excerpt || '',
    content: rawPost.content || '',
    author: rawPost.author || 'Carmen Lorraine',
    publishedAt,
    createdAt,
    updatedAt,
    published: rawPost.status ? rawPost.status === 'published' : Boolean(rawPost.published),
    featured: Boolean(rawPost.featured),
    categories,
    tags: [],
    heroImage,
    galleryImages,
    readingTime: Number.isFinite(rawPost.readingTime)
      ? Number(rawPost.readingTime)
      : Math.max(1, Math.round(String(rawPost.content || '').replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length / 220)),
    status: rawPost.status,
    scheduledFor: rawPost.scheduledFor || '',
    seoTitle: rawPost.seoTitle || '',
    metaDescription: rawPost.metaDescription || '',
    h1: rawPost.h1 || '',
    canonical: rawPost.canonical || '',
    noindex: Boolean(rawPost.noindex),
    category: categories[0] || '',
    faq: Array.isArray(rawPost.faq) ? rawPost.faq : [],
    cta: rawPost.cta || undefined,
    galleryCategory: rawPost.galleryCategory || '',
    galleryIds: Array.isArray(rawPost.galleryIds) ? rawPost.galleryIds.map((id: any) => String(id)) : [],
    showGalleryBlock: rawPost.showGalleryBlock !== false,
    galleryCount: Math.min(12, Math.max(1, Math.round(Number(rawPost.galleryCount) || 6))),
    featuredImageAlt: rawPost.featuredImageAlt || heroImage?.alt || '',
    showToc: rawPost.showToc !== false,
  };
  if (heroImage && basePost.featuredImageAlt) heroImage.alt = basePost.featuredImageAlt;
  return basePost;
};

export const getBlogPostsFromServer = async (retryCount = 0): Promise<BlogPost[]> => {
  try {
    console.log(`📝 Fetching blog posts from server... (attempt ${retryCount + 1})`);
    // Add cache-busting parameter to ensure fresh data
    const cacheBuster = Date.now();
    const response = await fetch(`${API_BASE_URL}/blog-posts?_cb=${cacheBuster}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const posts = await response.json();
    console.log('📝 Server returned blog posts:', posts.length);
    
    // Format posts to ensure they have all required fields
    const formattedPosts = studioOrder(posts.map((post: any) => normalizeBlogPost(post)));
    
    console.log('📝 Formatted server blog posts:', formattedPosts.length);
    return formattedPosts;
  } catch (error) {
    console.error(`Error fetching blog posts (attempt ${retryCount + 1}):`, error);
    
    // Retry logic
    if (retryCount < 2) {
      console.log(`📝 Retrying blog posts fetch in 1 second... (attempt ${retryCount + 2})`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return getBlogPostsFromServer(retryCount + 1);
    }
    
    console.log('📝 All retry attempts failed, returning empty array');
    return [];
  }
};

export const getBlogCategoriesFromServer = async (retryCount = 0): Promise<BlogCategory[]> => {
  try {
    console.log(`📚 Fetching blog categories from server... (attempt ${retryCount + 1})`);
    // Add cache-busting parameter to ensure fresh data
    const cacheBuster = Date.now();
    const response = await fetch(`${API_BASE_URL}/blog-categories?_cb=${cacheBuster}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const categories = await response.json();
    console.log('📚 Server returned blog categories:', categories.length);
    
    // Format categories to ensure they have all required fields
    const formattedCategories = categories.map((cat: any) => ({
      id: cat.id || cat._id?.toString(),
      name: cat.name,
      slug: cat.slug,
      description: cat.description || cat.intro || '',
      color: cat.color || '#8B4513',
      intro: cat.intro || cat.description || '',
      seoTitle: cat.seoTitle || '',
      metaDescription: cat.metaDescription || '',
      h1: cat.h1 || cat.name,
      createdAt: cat.createdAt,
      updatedAt: cat.updatedAt,
      order: cat.order || 0
    }));
    
    console.log('📚 Formatted server blog categories:', formattedCategories.length);
    return formattedCategories;
  } catch (error) {
    console.error(`Error fetching blog categories (attempt ${retryCount + 1}):`, error);
    
    // Retry logic
    if (retryCount < 2) {
      console.log(`📚 Retrying blog categories fetch in 1 second... (attempt ${retryCount + 2})`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return getBlogCategoriesFromServer(retryCount + 1);
    }
    
    console.log('📚 All retry attempts failed, returning empty array');
    return [];
  }
};

export const saveBlogPostsToServer = async (posts: BlogPost[]): Promise<boolean> => {
  try {
    const normalizedPosts = posts.map(post => normalizeBlogPost(post));
    console.log('📝 Saving blog posts to server:', normalizedPosts.length);
    const response = await fetch(`${API_BASE_URL}/blog-posts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(normalizedPosts),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('📝 Blog posts saved to server successfully:', result);
    return true;
  } catch (error) {
    console.error('Error saving blog posts to server:', error);
    return false;
  }
};

export const saveBlogCategoriesToServer = async (categories: BlogCategory[]): Promise<boolean> => {
  try {
    console.log('📚 Saving blog categories to server:', categories.length);
    const response = await fetch(`${API_BASE_URL}/blog-categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(categories),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('📚 Blog categories saved to server successfully:', result);
    return true;
  } catch (error) {
    console.error('Error saving blog categories to server:', error);
    return false;
  }
};

// Live mode loading function (like GalleryAPI)
export const loadLiveBlogData = async (): Promise<{ posts: BlogPost[]; categories: BlogCategory[] }> => {
  console.log('📝 LIVE MODE: Loading blog data from server only...');
  
  try {
    const [posts, categories] = await Promise.all([
      getBlogPostsFromServer(),
      getBlogCategoriesFromServer()
    ]);
    
    console.log('✅ LIVE: Using server blog data:', { posts: posts.length, categories: categories.length });
    return { posts, categories };
  } catch (error) {
    console.error('❌ LIVE: Failed to load blog data from server:', error);
    return { posts: [], categories: [] };
  }
};
